import { X, ChevronDown, Upload, FileText, FileSpreadsheet, Presentation, Trash2, Image } from 'lucide-react';
import { useNavigation } from '@/contexts/NavigationContext';
import { useRef, useState, useCallback } from 'react';
import { useModels } from '@/hooks/useModels';
import ModelSelectorModal from '@/components/modals/ModelSelectorModal';
import { useProjectFiles, useProjectFileStats, useUploadProjectFile, useDeleteProjectFile } from '@/hooks/useProjectFiles';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { ContextBudgetIndicator } from '@/components/ContextBudgetIndicator';

import {
  ACCEPT_ALL,
  MAX_PROJECT_FILES,
  MAX_PROJECT_TOKENS,
  isSupportedExtension,
  formatFileSize,
  validateUploadSize,
} from '@/lib/fileConstants';

function getFileIcon(fileType: string) {
  switch (fileType) {
    case 'xlsx':
    case 'csv':
      return <FileSpreadsheet className="w-4 h-4 text-green-500 shrink-0" />;
    case 'pptx':
      return <Presentation className="w-4 h-4 text-orange-500 shrink-0" />;
    case 'image':
      return <Image className="w-4 h-4 text-purple-500 shrink-0" />;
    default:
      return <FileText className="w-4 h-4 text-blue-500 shrink-0" />;
  }
}

function progressColor(ratio: number): string {
  if (ratio >= 1) return 'bg-red-500';
  if (ratio >= 0.8) return 'bg-yellow-500';
  return 'bg-brand-primary';
}

export const ConfigPanel = () => {
  const { configPanelOpen, setConfigPanelOpen, navigation } = useNavigation();
  const { data: models } = useModels();
  const [activeTab, setActiveTab] = useState<'geral' | 'instrucoes' | 'conhecimento'>('instrucoes');
  const [systemPrompt, setSystemPrompt] = useState('Você é um assistente especializado em...');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);

  const projectId = navigation.projectId;
  const { data: files, isLoading: filesLoading } = useProjectFiles(projectId);
  const { data: stats } = useProjectFileStats(projectId);
  const uploadFile = useUploadProjectFile();
  const deleteFile = useDeleteProjectFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = useCallback((file: File) => {
    if (!projectId) return;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!isSupportedExtension(file.name)) {
      toast.error(`Tipo de arquivo não suportado: .${ext}`);
      return;
    }
    const sizeError = validateUploadSize(file);
    if (sizeError) {
      toast.error(sizeError);
      return;
    }
    if ((files?.length ?? 0) >= MAX_PROJECT_FILES) {
      toast.error(`Limite de ${MAX_PROJECT_FILES} arquivos por projeto atingido`);
      return;
    }
    uploadFile.mutate({ projectId, file }, {
      onSuccess: () => toast.success(`"${file.name}" enviado com sucesso`),
      onError: (err) => toast.error(`Erro ao enviar: ${err.message}`),
    });
  }, [projectId, files?.length, uploadFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    Array.from(e.dataTransfer.files).forEach((f) => handleFileSelect(f));
  }, [handleFileSelect]);

  const handleDeleteFile = useCallback((fileId: string, storagePath: string, fileName: string) => {
    if (!projectId) return;
    deleteFile.mutate({ fileId, storagePath, projectId }, {
      onSuccess: () => toast.success(`"${fileName}" removido`),
      onError: (err) => toast.error(`Erro ao remover: ${err.message}`),
    });
  }, [projectId, deleteFile]);

  if (!configPanelOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={() => setConfigPanelOpen(false)}
      />
      
      <div
        className={`fixed top-0 right-0 bottom-0 w-[400px] bg-surface shadow-2xl z-50 transform transition-transform duration-300 ${
          configPanelOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-color flex items-center justify-between">
            <h2 className="text-lg font-semibold text-primary">⚙️ Configurações</h2>
            <button
              onClick={() => setConfigPanelOpen(false)}
              className="p-2 hover:bg-hover rounded-md"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="border-b border-color px-6 py-3 flex gap-2">
            <button
              onClick={() => setActiveTab('geral')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'geral' ? 'bg-active text-primary' : 'text-secondary hover:bg-hover'
              }`}
            >
              📋 Geral
            </button>
            <button
              onClick={() => setActiveTab('instrucoes')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'instrucoes' ? 'bg-active text-primary' : 'text-secondary hover:bg-hover'
              }`}
            >
              🧠 Instruções
            </button>
            <button
              onClick={() => setActiveTab('conhecimento')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'conhecimento' ? 'bg-active text-primary' : 'text-secondary hover:bg-hover'
              }`}
            >
              📚 Base Conhec.
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'geral' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Nome</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 rounded-md border border-color bg-surface text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Nome do chat"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Descrição</label>
                  <textarea
                    className="w-full px-3 py-2 rounded-md border border-color bg-surface text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
                    rows={3}
                    placeholder="Descrição opcional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Status</label>
                  <select className="w-full px-3 py-2 rounded-md border border-color bg-surface text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary">
                    <option>Ativo</option>
                    <option>Inativo</option>
                  </select>
                </div>
              </div>
            )}

            {activeTab === 'instrucoes' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Prompt do sistema
                  </label>
                  <textarea
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-color bg-surface text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
                    rows={8}
                    placeholder="Você é um assistente especializado em..."
                  />
                  <ContextBudgetIndicator value={systemPrompt} modelId={selectedModel || undefined} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Modelo padrão
                  </label>
                  <button
                    type="button"
                    onClick={() => setModelSelectorOpen(true)}
                    className="w-full px-3 py-2 rounded-md border border-color bg-surface text-primary text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  >
                    <span>{models?.find(m => m.id === selectedModel)?.display_name ?? 'Selecionar modelo'}</span>
                    <ChevronDown size={16} className="text-secondary" />
                  </button>
                  <ModelSelectorModal
                    open={modelSelectorOpen}
                    onOpenChange={setModelSelectorOpen}
                    value={selectedModel}
                    onSelect={setSelectedModel}
                  />
                </div>
              </div>
            )}

            {activeTab === 'conhecimento' && (
              <div className="space-y-4">
                {!projectId ? (
                  <p className="text-sm text-secondary text-center py-8">
                    Selecione um projeto para gerenciar a base de conhecimento
                  </p>
                ) : (
                  <>
                    {/* Usage bars */}
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-xs text-secondary mb-1">
                          <span>Arquivos</span>
                          <span>{stats?.file_count ?? 0} / {MAX_PROJECT_FILES}</span>
                        </div>
                        <div className="h-2 rounded-full bg-secondary overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${progressColor((stats?.file_count ?? 0) / MAX_PROJECT_FILES)}`}
                            style={{ width: `${Math.min(100, ((stats?.file_count ?? 0) / MAX_PROJECT_FILES) * 100)}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs text-secondary mb-1">
                          <span>Tokens</span>
                          <span>{(stats?.total_tokens ?? 0).toLocaleString()} / {MAX_PROJECT_TOKENS.toLocaleString()}</span>
                        </div>
                        <div className="h-2 rounded-full bg-secondary overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${progressColor((stats?.total_tokens ?? 0) / MAX_PROJECT_TOKENS)}`}
                            style={{ width: `${Math.min(100, ((stats?.total_tokens ?? 0) / MAX_PROJECT_TOKENS) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Dropzone */}
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                        isDragging ? 'border-brand-primary bg-hover' : 'border-color hover:bg-hover'
                      }`}
                    >
                      <Upload className={`w-8 h-8 ${isDragging ? 'text-brand-primary' : 'text-secondary'}`} />
                      <span className="text-sm text-secondary">Arraste arquivos ou clique para selecionar</span>
                      <span className="text-xs text-tertiary">PDF, DOCX, PPTX, XLSX, TXT, MD, CSV, PNG, JPG, GIF, WebP — máx. 128 MB</span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT_ALL}
                        className="hidden"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files ?? []);
                          files.forEach((f) => handleFileSelect(f));
                          e.target.value = '';
                        }}
                      />
                    </div>

                    {/* File list */}
                    <div>
                      <label className="block text-sm font-medium text-primary mb-2">Arquivos</label>
                      {filesLoading ? (
                        <p className="text-sm text-secondary">Carregando...</p>
                      ) : !files?.length ? (
                        <p className="text-sm text-secondary text-center py-4">Nenhum arquivo na base de conhecimento</p>
                      ) : (
                        <div className="space-y-1.5">
                          {files.map((file) => (
                            <div key={file.id} className="group flex items-center gap-2 p-2.5 rounded-lg bg-secondary hover:bg-hover transition-colors">
                              {getFileIcon(file.file_type)}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-primary truncate">{file.file_name}</p>
                                <p className="text-xs text-tertiary">{formatFileSize(file.file_size_bytes)}</p>
                              </div>
                              <TooltipProvider>
                                {file.parsing_status === 'pending' && (
                                  <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-600 border-yellow-500/30 shrink-0">Pendente</Badge>
                                )}
                                {file.parsing_status === 'processing' && (
                                  <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/30 animate-pulse shrink-0">Processando</Badge>
                                )}
                                {file.parsing_status === 'done' && (
                                  <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/30 shrink-0">
                                    {file.token_count?.toLocaleString()} tokens
                                  </Badge>
                                )}
                                {file.parsing_status === 'failed' && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant="outline" className="text-xs bg-red-500/10 text-red-600 border-red-500/30 cursor-help shrink-0">Falhou</Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-[200px]">
                                      <p className="text-xs">{file.parsing_error || 'Erro desconhecido'}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </TooltipProvider>
                              <button
                                onClick={() => handleDeleteFile(file.id, file.storage_path, file.file_name)}
                                className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-active transition-all"
                                disabled={deleteFile.isPending}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-color flex gap-3">
            <button
              onClick={() => setConfigPanelOpen(false)}
              className="flex-1 px-4 py-2 border border-color rounded-md text-sm font-medium text-primary hover:bg-hover"
            >
              Cancelar
            </button>
            <button className="flex-1 px-4 py-2 brand-primary rounded-md text-sm font-medium">
              Salvar
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
